// Bolhas dos pins BCF: os pins que ficam juntos no ecrã passam a uma
// só marca, com o número de tópicos.
//
// Existe por causa do Cha139: 1191 tópicos importados, concentrados numa
// zona do edifício, desenhavam uma mancha de marcadores maior do que o
// próprio edifício. Cada pin estava no sítio certo, mas juntos deixavam
// de dizer onde. Com as bolhas, de longe vê-se "312 aqui, 40 ali"; ao
// aproximar, as bolhas desfazem-se em pins.
//
// Puro: recebe posições em pixels do ecrã e devolve as bolhas. A
// projecção e o desenho estão no BcfPinsOverlay.

import { pesoDaPrioridade } from '@modules/bcf/lib/bcfAgrupar'

/** Distância, em pixels do ecrã, abaixo da qual dois pins se juntam
 *  numa bolha. Um pouco mais do que o ícone do pin (16 px com margem):
 *  abaixo disto os ícones sobrepõem-se e já não se distinguem. */
export const RAIO_DA_BOLHA_PX = 28

/** O mínimo que o agrupamento precisa de cada pin. */
export interface PontoNoEcra {
  /** Identidade do pin, estável entre fotogramas. */
  chave: string
  x: number
  y: number
}

export interface Bolha<T extends PontoNoEcra> {
  /** Chave estável da bolha: a do primeiro pin (o que a fundou). */
  chave: string
  /** Centro: a média das posições dos pins. */
  x: number
  y: number
  pins: T[]
}

/**
 * Junta os pins em bolhas, por grelha de ecrã.
 *
 * A grelha tem células do tamanho do raio. Cada pin, pela ordem dada,
 * procura nas nove células à volta da sua uma bolha cujo fundador esteja
 * a menos de `raio`; se a encontra, entra nela; senão funda uma nova na
 * sua célula. Cada pin olha para um número limitado de bolhas (as das
 * nove células), por isso o custo é linear no número de pins.
 *
 * A distância conta-se ao FUNDADOR e não ao centro, que se mexe à medida
 * que os pins entram: assim o resultado só depende da ordem de entrada, e
 * quem chama dá uma ordem estável (a dos tópicos) para as bolhas não
 * saltarem de um fotograma para o outro.
 */
export function agruparEmBolhas<T extends PontoNoEcra>(
  pins: readonly T[],
  raio: number = RAIO_DA_BOLHA_PX,
): Bolha<T>[] {
  const lado = raio > 0 ? raio : 1
  const raio2 = raio * raio
  const grelha = new Map<string, number[]>()
  const fundadores: { x: number; y: number }[] = []
  const membros: T[][] = []
  for (const p of pins) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue
    const cx = Math.floor(p.x / lado)
    const cy = Math.floor(p.y / lado)
    let escolhida = -1
    let melhor = Number.POSITIVE_INFINITY
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const lista = grelha.get(`${cx + dx}:${cy + dy}`)
        if (!lista) continue
        for (const i of lista) {
          const f = fundadores[i]
          const d2 = (f.x - p.x) ** 2 + (f.y - p.y) ** 2
          if (d2 < raio2 && d2 < melhor) {
            melhor = d2
            escolhida = i
          }
        }
      }
    }
    if (escolhida >= 0) {
      membros[escolhida].push(p)
      continue
    }
    const i = fundadores.length
    fundadores.push({ x: p.x, y: p.y })
    membros.push([p])
    const k = `${cx}:${cy}`
    const lista = grelha.get(k)
    if (lista) lista.push(i)
    else grelha.set(k, [i])
  }
  return membros.map((ps) => {
    let sx = 0
    let sy = 0
    for (const p of ps) {
      sx += p.x
      sy += p.y
    }
    return { chave: ps[0].chave, x: sx / ps.length, y: sy / ps.length, pins: ps }
  })
}

// --- Cor ---------------------------------------------------------------------------

/** Tom de uma marca (pin ou bolha), para a classe do Tailwind. */
export type TomDaMarca = 'critica' | 'alta' | 'normal' | 'baixa' | 'sem-prioridade' | 'fechado'

/**
 * A cor de uma marca: a prioridade mais alta dos tópicos ABERTOS que lá
 * estão; verde quando estão todos fechados.
 *
 * Escolheu-se a prioridade e não o estado dominante porque é a pergunta
 * que uma bolha tem de responder de longe: "há aqui alguma coisa grave?".
 * Um estado dominante escondia um conflito crítico no meio de quarenta
 * normais; a prioridade mais alta não o deixa passar. Os fechados não
 * contam para a cor enquanto houver abertos: um crítico já resolvido não
 * deve pintar de vermelho uma bolha de pendentes ligeiros.
 *
 * As prioridades reconhecidas são as de `pesoDaPrioridade` (módulo BCF,
 * em português e em inglês); as outras contam como "sem prioridade".
 */
export function tomDaMarca(
  topicos: readonly { priority?: string | null; closed: boolean }[],
): TomDaMarca {
  let peso = -1
  let abertos = 0
  for (const t of topicos) {
    if (t.closed) continue
    abertos += 1
    peso = Math.max(peso, pesoDaPrioridade(t.priority ?? null))
  }
  if (abertos === 0) return topicos.length > 0 ? 'fechado' : 'sem-prioridade'
  if (peso >= 4) return 'critica'
  if (peso === 3) return 'alta'
  if (peso === 2) return 'normal'
  if (peso === 1) return 'baixa'
  return 'sem-prioridade'
}

/** Classes do Tailwind de cada tom (fundo, contorno, texto). O tom sem
 *  prioridade é o âmbar que os pins sempre tiveram. */
export const CLASSES_DO_TOM: Record<TomDaMarca, string> = {
  critica: 'border-red-200 bg-red-600 text-white',
  alta: 'border-orange-200 bg-orange-500 text-slate-950',
  normal: 'border-yellow-100 bg-yellow-400 text-slate-950',
  baixa: 'border-sky-100 bg-sky-400 text-slate-950',
  'sem-prioridade': 'border-amber-200 bg-amber-500 text-slate-950',
  fechado: 'border-emerald-200 bg-emerald-500 text-slate-950',
}

export const ROTULO_DO_TOM: Record<TomDaMarca, string> = {
  critica: 'Crítica',
  alta: 'Alta',
  normal: 'Normal',
  baixa: 'Baixa',
  'sem-prioridade': 'Sem prioridade',
  fechado: 'Todos fechados',
}

/** Diâmetro da bolha em pixels: cresce com a ordem de grandeza da
 *  contagem, para "1191" caber sem a bolha de "3" ficar enorme. */
export function diametroDaBolha(n: number): number {
  if (n < 10) return 22
  if (n < 100) return 26
  if (n < 1000) return 32
  return 38
}

// --- Clicar numa bolha -------------------------------------------------------------

export type Ponto3 = readonly [number, number, number]

export interface Caixa3 {
  min: [number, number, number]
  max: [number, number, number]
}

/** A caixa das âncoras dos pins, ou null sem pins. */
export function caixaDasAncoras(ancoras: readonly Ponto3[]): Caixa3 | null {
  if (ancoras.length === 0) return null
  const min: [number, number, number] = [Infinity, Infinity, Infinity]
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity]
  for (const a of ancoras) {
    for (let i = 0; i < 3; i++) {
      if (a[i] < min[i]) min[i] = a[i]
      if (a[i] > max[i]) max[i] = a[i]
    }
  }
  return { min, max }
}

/** Abaixo desta diagonal (metros) as âncoras estão praticamente no mesmo
 *  ponto: aproximar não as separa, e o clique abre a lista. */
export const SEPARACAO_MINIMA_M = 0.25

/** Lado mínimo, em metros, da caixa para onde a câmara vai: aproximar a
 *  uma caixa de poucos centímetros punha a câmara colada às âncoras. */
export const LADO_MINIMO_DO_ENQUADRAMENTO_M = 3

/** A caixa a enquadrar: a das âncoras, alargada até ao lado mínimo em
 *  cada eixo, à volta do mesmo centro. */
export function caixaAEnquadrar(c: Caixa3, ladoMinimo = LADO_MINIMO_DO_ENQUADRAMENTO_M): Caixa3 {
  const min: [number, number, number] = [...c.min]
  const max: [number, number, number] = [...c.max]
  for (let i = 0; i < 3; i++) {
    const lado = max[i] - min[i]
    if (lado < ladoMinimo) {
      const meio = (min[i] + max[i]) / 2
      min[i] = meio - ladoMinimo / 2
      max[i] = meio + ladoMinimo / 2
    }
  }
  return { min, max }
}

/** Chave de um conjunto de pins, independente da ordem. */
export function chaveDoConjunto(chaves: readonly string[]): string {
  return [...chaves].sort().join('\u0000')
}

/**
 * O que faz o clique numa bolha: aproximar a câmara às âncoras, ou abrir
 * a lista dos tópicos para escolher.
 *
 * Abre a lista quando aproximar não separa: as âncoras cabem numa caixa
 * com diagonal abaixo de `SEPARACAO_MINIMA_M` (vários tópicos sobre o
 * mesmo ponto, como os conflitos de um mesmo cruzamento), ou a câmara já
 * foi aproximada a esta mesma bolha no clique anterior e ela não se
 * desfez.
 */
export function acaoDaBolha(o: {
  ancoras: readonly Ponto3[]
  chaves: readonly string[]
  ultimaAproximada: string | null
}): { tipo: 'aproximar'; caixa: Caixa3 } | { tipo: 'lista' } {
  const caixa = caixaDasAncoras(o.ancoras)
  if (!caixa) return { tipo: 'lista' }
  const diagonal = Math.hypot(caixa.max[0] - caixa.min[0], caixa.max[1] - caixa.min[1], caixa.max[2] - caixa.min[2])
  if (diagonal < SEPARACAO_MINIMA_M) return { tipo: 'lista' }
  if (o.ultimaAproximada !== null && o.ultimaAproximada === chaveDoConjunto(o.chaves)) return { tipo: 'lista' }
  return { tipo: 'aproximar', caixa: caixaAEnquadrar(caixa) }
}
